import { ILocalDeliverable } from "@entities/deliverableFormats/ui/DeliverableFormats";
import { FirebaseService, ToastService } from "@shared/lib/services";
import { IPurchaseItems } from "@shared/lib/types";
import { useEffect, useMemo, useState } from "react";
import { useDebounce } from "use-debounce";
import { useCreateAlterationRequest } from "../lib/hooks/useCreateAlterationRequest";
import useGetAlterationPrerequisites from "../lib/hooks/useGetAlterationPrerequisites";
import { useGetBriefCampaignInfluner } from "../lib/hooks/useGetBriefCampaignInfluner";
import { ICampaignAlterationApprovalRequest, ICampaignAlterationRequestPayload } from "../lib/types";

export interface CommercialTaxSettings {
  creatorGstEnabled: boolean;
  creatorGstPercent: number;
  creatorTdsEnabled: boolean;
  creatorTdsPercent: number;
  brandGstEnabled: boolean;
  brandGstPercent: number;
  brandServiceCostEnabled: boolean;
  brandServiceCostRate: number;
}

export interface ProofAttachment {
  id: string;
  name: string;
  url?: string;
  file?: File;
}

export const DEFAULT_COMMERCIAL_TAX_SETTINGS: CommercialTaxSettings = {
  creatorGstEnabled: true,
  creatorGstPercent: 18,
  creatorTdsEnabled: true,
  creatorTdsPercent: 10,
  brandGstEnabled: true,
  brandGstPercent: 18,
  brandServiceCostEnabled: true,
  brandServiceCostRate: 0,
};

export const formatSignedInr = (amount: number | null | undefined): string => {
  if (amount == null) return '—';
  if (amount < 0) {
    return `-₹${Math.abs(amount).toLocaleString('en-IN')}`;
  }
  return `+₹${amount.toLocaleString('en-IN')}`;
};

const generateObjectId = () => {
  const timestamp = Math.floor(new Date().getTime() / 1000).toString(16).padStart(8, '0');
  const random = Array.from({ length: 16 }, () => Math.floor(Math.random() * 16).toString(16)).join('');
  return timestamp + random;
};

const getConfiguratorId = (item: IPurchaseItems) => {
  return item.contentDeliverableConfiguratorId || "";
};

const getDeliverableId = (item: IPurchaseItems) => {
  return item.contentDeliverableConfiguratorDeliverableId || "";
};

const getLinkedContentId = (item: IPurchaseItems) => {
  return item.adRightsLinkedToContentPurchaseItemId || "";
};

export const filesToProofAttachments = (files: File[]): ProofAttachment[] => {
  return files.map((file) => ({
    id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    name: file.name,
    file,
  }));
};

export function computeCommercialChangePreview({
  baseInfluencer,
  baseBrand,
  influencerDelta,
  brandDelta,
  influencerDirection,
  brandDirection,
  tax,
}: {
  baseInfluencer: number | null;
  baseBrand: number | null;
  influencerDelta: number;
  brandDelta: number;
  influencerDirection: 'add' | 'subtract';
  brandDirection: 'add' | 'subtract';
  tax: CommercialTaxSettings;
}) {
  const signedInfluencerDelta = influencerDirection === 'add' ? influencerDelta : -influencerDelta;
  const signedBrandDelta = brandDirection === 'add' ? brandDelta : -brandDelta;
  const newInfluencer = baseInfluencer !== null ? baseInfluencer + signedInfluencerDelta : null;
  const newBrand = baseBrand !== null ? baseBrand + signedBrandDelta : null;

  const creatorGstAmount = tax.creatorGstEnabled ? signedInfluencerDelta * (tax.creatorGstPercent / 100) : 0;
  const creatorTdsAmount = tax.creatorTdsEnabled ? signedInfluencerDelta * (tax.creatorTdsPercent / 100) : 0;
  const brandGstAmount = tax.brandGstEnabled ? signedBrandDelta * (tax.brandGstPercent / 100) : 0;

  const influencerLedgerDelta = signedInfluencerDelta + creatorGstAmount - creatorTdsAmount;
  const brandLedgerDelta = signedBrandDelta + brandGstAmount;

  return {
    signedInfluencerDelta,
    signedBrandDelta,
    newInfluencer,
    newBrand,
    creatorGstAmount,
    creatorTdsAmount,
    brandGstAmount,
    influencerLedgerDelta,
    brandLedgerDelta,
  };
}

export function useChangeDeliverablesForm({
  campaignInfluencerId,
  briefId,
  onSuccess,
  viewOnly,
  alterationRequest,
}: {
  campaignInfluencerId: string;
  briefId?: string;
  onSuccess?: () => void;
  viewOnly?: boolean;
  alterationRequest?: ICampaignAlterationApprovalRequest;
}) {
  const [influencerDirection, setInfluencerDirection] = useState<'add' | 'subtract'>(
    alterationRequest ? (alterationRequest.influencerCostDirection || 'add') : 'add'
  );
  const [brandDirection, setBrandDirection] = useState<'add' | 'subtract'>(
    alterationRequest ? (alterationRequest.brandCostDirection || 'add') : 'add'
  );
  const [influencerDelta, setInfluencerDelta] = useState(
    alterationRequest ? String(alterationRequest.influencerCostBaseCost || 0) : ''
  );
  const [brandDelta, setBrandDelta] = useState(
    alterationRequest ? String(alterationRequest.brandCostBaseCost || 0) : ''
  );
  const [remarks, setRemarks] = useState(
    alterationRequest ? alterationRequest.remarks : ''
  );
  const [tax, setTax] = useState<CommercialTaxSettings>({ ...DEFAULT_COMMERCIAL_TAX_SETTINGS });
  const [showAdvancedTax, setShowAdvancedTax] = useState(!!alterationRequest);
  const [proofAttachments, setProofAttachments] = useState<ProofAttachment[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [proofUploadUrl, setProofUploadUrl] = useState<string>("");

  // Deliverables states
  const [deliverables, setDeliverables] = useState<ILocalDeliverable[]>([]);
  const [deletedPurchaseItems, setDeletedPurchaseItems] = useState<IPurchaseItems[]>([]);

  const isCompletedStatus =
    alterationRequest?.status === "Approved" || alterationRequest?.status === "Rejected";

  const { briefInfluencer, campaignInfluencer, isFetching: isLoadingInf } = useGetBriefCampaignInfluner({
    campaignInfluencerId,
    enabled: !!campaignInfluencerId && !isCompletedStatus,
  });

  const initialPrerequisitePayload: ICampaignAlterationRequestPayload = useMemo(
    () => ({
      campaignInfluencerId,
      type: "Deliverable-Adjustment",
      toAlterBrandCost: false,
      brandCostBaseCost: 0,
      brandCostDirection: "add",
      applyBrandCostServiceCostRate: true,
      applyBrandCostGstRate: true,
      toAlterInfluencerCost: false,
      influencerCostBaseCost: 0,
      influencerCostDirection: "add",
      applyInfluencerCostGstRate: true,
      applyInfluencerCostTdsRate: true,
      remarks: "",
    }),
    [campaignInfluencerId]
  );

  const { alterationPrerequisite: initialTaxPrerequisite } = useGetAlterationPrerequisites(
    initialPrerequisitePayload,
    !!campaignInfluencerId && !isCompletedStatus
  );

  useEffect(() => {
    const prereqData = initialTaxPrerequisite?.data?.new || initialTaxPrerequisite?.data?.old;
    if (!prereqData) return;

    setTax((prev) => ({
      ...prev,
      creatorGstPercent: prereqData.influencerCostGstRate ?? prev.creatorGstPercent,
      creatorTdsPercent: prereqData.influencerCostTdsRate ?? prev.creatorTdsPercent,
      brandGstPercent: prereqData.brandCostGstRate ?? prev.brandGstPercent,
      brandServiceCostRate: prereqData.brandCostServiceCostRate ?? prev.brandServiceCostRate,
    }));
  }, [initialTaxPrerequisite?.data]);

  const { createAlteration, isCreating } = useCreateAlterationRequest(briefId, onSuccess);

  // Stabilize the purchaseItems array reference — without this, every React Query
  // render produces a new array reference even for identical cached data, which
  // causes the useEffect below to fire on every render and loop infinitely.
  const currentPurchaseItems = useMemo(
    () => {
      const isCompletedStatus =
        alterationRequest?.status === "Approved" || alterationRequest?.status === "Rejected";
      const source = isCompletedStatus
        ? alterationRequest?.preChangedDeliverablePurchaseItems
        : campaignInfluencer?.purchaseItems;
      return source ?? null;
    },
    [
      campaignInfluencer?.purchaseItems,
      alterationRequest?.status,
      alterationRequest?.preChangedDeliverablePurchaseItems,
    ]
  );

  // Populate local deliverables on mount or when currentPurchaseItems changes
  useEffect(() => {
    if (currentPurchaseItems) {
      const parsedItems: ILocalDeliverable[] = [];
      const adRightsGroupMap: { [key: string]: ILocalDeliverable } = {};

      currentPurchaseItems.forEach((item) => {
        const isAdRights =
          item.purchaseItemType === "Ad-Rights" ||
          item.contentCategory === "Ad Rights";

        if (isAdRights) {
          const linkedId = getLinkedContentId(item);
          const newItem: ILocalDeliverable = {
            clientSideId: item._id || Math.random().toString(36).substring(2, 9),
            _id: item._id,
            purchaseItemType: "Ad-Rights",
            contentCategory: "Ad Rights",
            contentSubType: item.contentSubType || {},
            contentType: item.contentType || "",
            adRightsDurations: item.adRightsDurations || "",
            adRightsNotes: item.adRightsNotes || "",
            adRightsType: item.adRightsType || "",
            adRightsDescription: item.adRightsDescription || "",
            contentDeliverableConfiguratorId: getConfiguratorId(item),
            contentDeliverableConfiguratorDeliverableId: getDeliverableId(item),
            contentDeliverableConfigurator: item.contentDeliverableConfigurator,
            adRightsLinkedToContentPurchaseItemId: linkedId,
            isExisting: true,
            isCancelled: item.isCancelled,
          };
          parsedItems.push(newItem);
        } else {
          parsedItems.push({
            clientSideId: item._id || Math.random().toString(36).substring(2, 9),
            _id: item._id,
            purchaseItemType: "Content",
            contentCategory: item.contentCategory || "",
            contentSubType: item.contentSubType || {},
            contentType: item.contentType || "",
            adRightsDurations: item.adRightsDurations || "",
            adRightsNotes: item.adRightsNotes || "",
            adRightsType: item.adRightsType || "",
            adRightsDescription: item.adRightsDescription || "",
            contentDeliverableConfiguratorId: getConfiguratorId(item),
            contentDeliverableConfiguratorDeliverableId: getDeliverableId(item),
            contentDeliverableConfigurator: item.contentDeliverableConfigurator,
            isExisting: true,
            isCancelled: item.isCancelled,
          });
        }
      });
      // if (viewOnly && alterationRequest) {
      //   const reqDeletedIds = (alterationRequest.deletedPurchaseItems || []).map((item) => item._id).filter(Boolean);
      //   const reqAddedItems: ILocalDeliverable[] = (alterationRequest.newlyAddedPurchaseItems || []).map((item) => ({
      //     clientSideId: item._id || Math.random().toString(36).substring(2, 9),
      //     _id: item._id,
      //     purchaseItemType: item.purchaseItemType,
      //     contentCategory: item.contentCategory || "",
      //     contentSubType: item.contentSubType || {},
      //     contentType: item.contentType || "",
      //     adRightsDurations: item.adRightsDurations || "",
      //     adRightsNotes: item.adRightsNotes || "",
      //     adRightsType: item.adRightsType || "",
      //     adRightsDescription: item.adRightsDescription || "",
      //     contentDeliverableConfiguratorId: getConfiguratorId(item),
      //     contentDeliverableConfiguratorDeliverableId: getDeliverableId(item),
      //     adRightsLinkedToContentPurchaseItemId: item.adRightsLinkedToContentPurchaseItemId,
      //     isExisting: true, // Mark as existing so it's read-only
      //   }));

      //   const filteredExisting = parsedItems.filter((item) => !item._id || !reqDeletedIds.includes(item._id));
      //   setDeliverables([...filteredExisting, ...reqAddedItems]);
      //   setDeletedPurchaseItems(alterationRequest.deletedPurchaseItems || []);
      // } else {
        setDeliverables(parsedItems);
        setDeletedPurchaseItems([]);
      // }
    }
  }, [currentPurchaseItems]);

  const payload: ICampaignAlterationRequestPayload = useMemo(() => ({
    campaignInfluencerId: campaignInfluencerId,
    type: "Deliverable-Adjustment",
    toAlterBrandCost: Number(brandDelta) > 0,
    brandCostBaseCost: Number(brandDelta) || 0,
    brandCostDirection: brandDirection,
    applyBrandCostGstRate: tax.brandGstEnabled,
    applyBrandCostServiceCostRate: tax.brandServiceCostEnabled,
    toAlterInfluencerCost: Number(influencerDelta) > 0,
    influencerCostBaseCost: Number(influencerDelta) || 0,
    influencerCostDirection: influencerDirection,
    applyInfluencerCostGstRate: tax.creatorGstEnabled,
    applyInfluencerCostTdsRate: tax.creatorTdsEnabled,
    remarks: remarks.trim(),
  }), [
    campaignInfluencerId,
    brandDelta,
    brandDirection,
    influencerDelta,
    influencerDirection,
    remarks,
    tax,
  ]);

  // Pass the stable memoized object directly — no () => wrapper, which would create
  // a new function reference every render and cause useDebounce to fire every 250ms.
  const [debouncedPayload] = useDebounce(payload, 250);

  const {
    alterationPrerequisite,
    isAlterationPrerequisiteFetching
  } = useGetAlterationPrerequisites(debouncedPayload, ((!alterationRequest || alterationRequest?.status === "Pending") && !isLoadingInf));

  const baseInfluencerCommercial = (!alterationRequest || alterationRequest?.status === "Pending")
    ? alterationPrerequisite?.data?.old?.totalInfluencerBaseCost || 0
    : alterationRequest?.preCheckResult?.old?.totalInfluencerBaseCost || 0;

  const baseBrandCommercial = (!alterationRequest || alterationRequest?.status === "Pending")
    ? alterationPrerequisite?.data?.old?.totalBrandSellingCost || 0
    : alterationRequest?.preCheckResult?.old?.totalBrandSellingCost || 0;

  const newInfluencerCommercial = (!alterationRequest || alterationRequest?.status === "Pending")
    ? alterationPrerequisite?.data?.new?.totalInfluencerBaseCost || 0
    : alterationRequest?.preCheckResult?.new?.totalInfluencerBaseCost || 0;

  const newBrandCommercial = (!alterationRequest || alterationRequest?.status === "Pending")
    ? alterationPrerequisite?.data?.new?.totalBrandSellingCost || 0
    : alterationRequest?.preCheckResult?.new?.totalBrandSellingCost || 0;

  const influencerCostGstRate = (!alterationRequest || alterationRequest?.status === "Pending")
    ? alterationPrerequisite?.data?.new?.influencerCostGstRate || 0
    : alterationRequest?.preCheckResult?.new?.influencerCostGstRate || 0;

  const brandCostGstRate = (!alterationRequest || alterationRequest?.status === "Pending")
    ? alterationPrerequisite?.data?.new?.brandCostGstRate || 0
    : alterationRequest?.preCheckResult?.new?.brandCostGstRate || 0;

  const influencerCostTdsRate = (!alterationRequest || alterationRequest?.status === "Pending")
    ? alterationPrerequisite?.data?.new?.influencerCostTdsRate || 0
    : alterationRequest?.preCheckResult?.new?.influencerCostTdsRate || 0;

  useEffect(() => {
    if (alterationRequest && !isCompletedStatus) {
      setTax((prev) => ({
        ...prev,
        creatorGstEnabled: alterationRequest.applyInfluencerCostGstRate ?? prev.creatorGstEnabled,
        creatorTdsEnabled: alterationRequest.applyInfluencerCostTdsRate ?? prev.creatorTdsEnabled,
        brandGstEnabled: alterationRequest.applyBrandCostGstRate ?? prev.brandGstEnabled,
        brandServiceCostEnabled: alterationRequest.applyBrandCostServiceCostRate ?? prev.brandServiceCostEnabled,
      }));
    }
  }, [alterationPrerequisite, alterationRequest]);

  useEffect(() => {
    if (isCompletedStatus && alterationRequest) {
      setTax((prev) => ({
        ...prev,
        creatorGstEnabled: alterationRequest.applyInfluencerCostGstRate ?? prev.creatorGstEnabled,
        creatorGstPercent: alterationRequest?.preCheckResult?.old?.influencerCostGstRate ?? prev.creatorGstPercent,
        creatorTdsEnabled: alterationRequest.applyInfluencerCostTdsRate ?? prev.creatorTdsEnabled,
        creatorTdsPercent: alterationRequest?.preCheckResult?.old?.influencerCostTdsRate ?? prev.creatorTdsPercent,
        brandGstEnabled: alterationRequest.applyBrandCostGstRate ?? prev.brandGstEnabled,
        brandGstPercent: alterationRequest?.preCheckResult?.old?.brandCostGstRate ?? prev.brandGstPercent,
        brandServiceCostEnabled: alterationRequest.applyBrandCostServiceCostRate ?? prev.brandServiceCostEnabled,
        brandServiceCostRate: alterationRequest?.preCheckResult?.old?.brandCostServiceCostRate ?? prev.brandServiceCostRate,
      }));
    } else if (!alterationRequest) {
      setTax((prev) => ({
        ...prev,
        creatorGstEnabled: true,
        creatorTdsEnabled: true,
        brandGstEnabled: true,
        brandServiceCostEnabled: true,
      }));
    }
  }, [isCompletedStatus, alterationRequest]);

  const commercialPreview = useMemo(
    () =>
      computeCommercialChangePreview({
        baseInfluencer: baseInfluencerCommercial,
        baseBrand: baseBrandCommercial,
        influencerDelta: Math.abs(baseInfluencerCommercial - newInfluencerCommercial),
        brandDelta: Math.abs(baseBrandCommercial - newBrandCommercial),
        influencerDirection,
        brandDirection,
        tax,
      }),
    [alterationPrerequisite, alterationRequest, influencerDelta, brandDelta, influencerDirection, brandDirection, tax]
  );

  const handleRemoveDeliverableIndex = (index: number) => {
    const itemToRemove = deliverables[index];
    if (itemToRemove.isExisting) {
      if (itemToRemove.purchaseItemType === "Ad-Rights") {
        const matchingItems = currentPurchaseItems?.filter((origItem) => {
          const isAdRights = origItem.purchaseItemType === "Ad-Rights";
          const matchType = origItem.adRightsType === itemToRemove.adRightsType;
          const matchDuration = origItem.adRightsDurations === itemToRemove.adRightsDurations;
          const matchNotes = origItem.adRightsNotes === itemToRemove.adRightsNotes;
          return isAdRights && matchType && matchDuration && matchNotes;
        }) || [];
        setDeletedPurchaseItems((prev) => [...prev, ...matchingItems]);
      } else {
        const matchingItem = currentPurchaseItems?.find((origItem) => origItem._id === itemToRemove._id);
        if (matchingItem) {
          setDeletedPurchaseItems((prev) => [...prev, matchingItem]);
        }
      }
    }
  };

  const processedItems = useMemo(() => {
    if (
      deliverables.some(
        (d) => d.purchaseItemType === "Content" && (!d.contentDeliverableConfiguratorId || !d.contentDeliverableConfiguratorDeliverableId)
      )
    ) {
      return null;
    }

    for (const d of deliverables) {
      if (d.purchaseItemType === "Ad-Rights" && (!d.adRightsType || !d.adRightsDurations)) {
        return null;
      }
    }

    const finalPurchaseItems: any[] = [];
    const contentMapByClientSideId: { [key: string]: any } = {};

    // 1. Content format items
    deliverables.forEach((d) => {
      if (d.purchaseItemType === "Content") {
        const item = {
          _id: d._id || generateObjectId(),
          purchaseItemType: "Content",
          contentCategory: d.contentCategory || "",
          contentSubType: d.contentSubType || {},
          contentType: d.contentType || "",
          contentDeliverableConfiguratorId: d.contentDeliverableConfiguratorId,
          contentDeliverableConfiguratorDeliverableId: d.contentDeliverableConfiguratorDeliverableId,
          contentDeliverableConfigurator: d.contentDeliverableConfiguratorId
        };
        finalPurchaseItems.push(item);
        contentMapByClientSideId[d.clientSideId] = item;
      }
    });

    // 2. Ad-Rights items
    deliverables.forEach((d) => {
      if (d.purchaseItemType === "Ad-Rights") {
        const adRightsType = d.adRightsType || "";
        const adRightsDurations = d.adRightsDurations || "";
        const adRightsNotes = d.adRightsNotes || "";
        const adRightsDescription = d.adRightsDescription || "";

        const baseAdRightsId = d._id || generateObjectId();
        const baseAdRightsItem: any = {
          purchaseItemType: "Ad-Rights",
          adRightsType,
          adRightsDurations,
          adRightsNotes,
          adRightsDescription,
          _id: baseAdRightsId,
        };

        const rawLinkedId = d.adRightsLinkedToContentPurchaseItemId || "";
        const linkedContentItem = rawLinkedId ? contentMapByClientSideId[rawLinkedId] : undefined;
        const linkedId = linkedContentItem ? linkedContentItem._id : rawLinkedId;

        if (linkedId === "" || !linkedId) {
          finalPurchaseItems.push(baseAdRightsItem);
        } else {
          finalPurchaseItems.push({
            ...baseAdRightsItem,
            adRightsLinkedToContentPurchaseItemId: linkedId || undefined,
          });
        }
      }
    });

    const newlyAddedPurchaseItems = finalPurchaseItems.filter((item) => {
      return !currentPurchaseItems?.some((orig) => orig._id === item._id);
    });

    return {
      newlyAddedPurchaseItems,
      deletedPurchaseItems,
    };
  }, [deliverables, deletedPurchaseItems, currentPurchaseItems]);

  const canSubmit =
    Number(influencerDelta) >= 0 && Number(brandDelta) >= 0 &&
    remarks.trim().length > 0 &&
    proofAttachments.length > 0 &&
    processedItems !== null &&
    (processedItems.newlyAddedPurchaseItems.length > 0 || processedItems.deletedPurchaseItems.length > 0);

  const handleBeforeUpload = async (file: File) => {
    setProofAttachments((prev) => [...filesToProofAttachments([file])]);
    try {
      setIsUploading(true);
      const uploadedUrl = await FirebaseService.uploadFileToFirebase(
        'alteration-proofs',
        file
      );
      setProofUploadUrl(uploadedUrl);
    } catch (error) {
      ToastService.error('Failed to upload proof file. Please try again.');
    } finally {
      setIsUploading(false);
    }
    return false; // Prevent antd default upload
  };

  const removeProofFile = (id: string) => {
    setProofAttachments((prev) => {
      const updated = prev.filter((f) => f.id !== id);
      if (updated.length === 0) {
        setProofUploadUrl('');
      }
      return updated;
    });
  };

  const handleConfirm = () => {
    if (!canSubmit || !processedItems) return;
    createAlteration({
      ...payload,
      proofUploadUrl,
      addDeliverablePurchaseItems: processedItems.newlyAddedPurchaseItems,
      cancelledDeliverablePurchaseItemIds: processedItems.deletedPurchaseItems.map((val) => val._id),
    });
  };

  return {
    influencerDirection,
    setInfluencerDirection,
    brandDirection,
    setBrandDirection,
    influencerDelta,
    setInfluencerDelta,
    brandDelta,
    setBrandDelta,
    remarks,
    setRemarks,
    tax,
    setTax,
    showAdvancedTax,
    setShowAdvancedTax,
    proofAttachments,
    isUploading,
    proofUploadUrl,
    deliverables,
    setDeliverables,
    deletedPurchaseItems,
    isAlterationPrerequisiteFetching,
    baseInfluencerCommercial,
    baseBrandCommercial,
    influencerCostGstRate,
    influencerCostTdsRate,
    brandCostGstRate,
    commercialPreview,
    processedItems,
    canSubmit,
    handleBeforeUpload,
    removeProofFile,
    handleConfirm,
    handleRemoveDeliverableIndex,
    isCreating,
    newInfluencerCommercial,
    newBrandCommercial,
    alterationPrerequisite,
    briefInfluencer,
    campaignInfluencer,
  };
}
